import { inject, Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../../environments/environment';

/**
 * Marca de build que viaja con la petición del diccionario.
 *
 * ⚠️ **Por qué existe.** Angular pone hash al nombre de los bundles de JS, pero
 * **no a los assets**: `assets/i18n/es.json` se llama igual antes y después de
 * un despliegue. El diccionario se sirve con `max-age=300` (ver los workflows
 * de deploy), así que durante esa ventana el navegador puede emparejar el JS
 * NUEVO con el diccionario VIEJO — y entonces toda clave nueva sale CRUDA en
 * pantalla (`cards.titularDocuments.title` literal), sin error y sin aviso.
 * Es lo que se vio en tpx2 el 2026-09-22.
 *
 * Con la marca en la consulta, la URL cambia en cuanto cambia el build: el
 * navegador no puede reutilizar la entrada anterior de su caché porque la clave
 * de caché incluye la consulta. Del lado de CloudFront el despliegue ya
 * invalida `/*`, así que lo que esto cierra es el tramo del navegador, que es
 * justo el que nadie puede purgar.
 *
 * ⚠️ **De dónde sale el valor, y por qué NO es `package.json#version`.** Esa
 * versión se sube a mano de vez en cuando, así que dos despliegues seguidos
 * compartirían marca y el diccionario viejo se seguiría sirviendo: el mismo
 * defecto, ahora con apariencia de estar resuelto. La marca la inyecta el
 * BUILD: los cuatro workflows de deploy escriben `buildId: '<sha del commit>'`
 * en su `environment.<amb>.ts`. El sha cambia con cada commit —o sea con
 * cualquier cambio del diccionario, que está versionado— y NO cambia al
 * re-desplegar el mismo commit, que es justo lo correcto: mismo contenido,
 * misma marca.
 *
 * Sin `buildId` —un build local, o un workflow que se olvidara de la llave— se
 * cae a la marca de ARRANQUE de la aplicación. Falla FRESCO y no rancio: como
 * mucho se paga una descarga del diccionario por carga de página; al revés se
 * pagaría con texto crudo en pantalla, que es el defecto que esto arregla.
 */
export const MARCA_DE_DICCIONARIO: string =
  (environment.buildId ?? '').trim() || `t${Date.now()}`;

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  getTranslation(lang: string) {
    return this.http.get<Translation>(
      `/assets/i18n/${lang}.json?v=${encodeURIComponent(MARCA_DE_DICCIONARIO)}`,
    );
  }
}
